import { useCallback, useEffect } from "react";
import {
  Box,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  ScrollArea,
  useWidgetViewState,
} from "@salestrak/ui";
import { AdvancedFilters } from "#app/components/filters/advanced-filters";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { issueFilterConfig } from "#app/lib/filters/configs/issue-filter-config";
import type { FilterState } from "#app/lib/filters/types";
import { useFilters } from "#app/lib/hooks/use-filters";

/** The advanced builder opens showing one empty row (matches the settings editor). */
const SEEDED_EMPTY_QUERY: FilterState = {
  logic: "AND",
  blocks: [{ logic: "AND", filters: [{ field: "", operator: "eq", value: null }] }],
};

/**
 * Shared header filter control that issue-backed widgets contribute via
 * `headerActions`: a transient per-viewer advanced-filter popover narrowing
 * this widget only, never persisted (persisted filters are Settings → Filters).
 * It publishes the applied `where` into the kit's `WidgetViewState`;
 * `useScopedWhere` folds it centrally so widget bodies need no wiring.
 * `entity-list` keeps its own header (Only-Me + filter).
 */
export function WidgetHeaderFilters() {
  const { patch } = useWidgetViewState();
  const query = useFilters(issueFilterConfig, {
    mode: "advanced",
    advancedState: SEEDED_EMPTY_QUERY,
  });
  const activeCount = query.activeFilterCount;

  useEffect(() => {
    // Active filters publish their built `where`; zero active rows clear it (a
    // half-built row shouldn't narrow the widget).
    patch({ filterWhere: activeCount > 0 ? query.where : null, filterCount: activeCount });
  }, [query.where, activeCount, patch]);

  const clearQuery = useCallback(() => {
    query.clearAll();
    query.addBlock();
  }, [query]);

  return (
    <Popover>
      {/* Face must match the sibling square ghost header buttons in widget-card.tsx. */}
      <PopoverTrigger
        render={
          <FilterToggle
            size="xs"
            shape="rounded"
            iconSize="xs"
            icon="filter"
            count={activeCount}
            onClear={clearQuery}
          />
        }
      />
      {/* The builder is an edge-to-edge form; the stock popover viewport insets
        * its content, so the padding is zeroed at the viewport slot. */}
      <PopoverPopup
        align="end"
        padding="none"
        className="w-[360px]"
      >
        <Box direction="column" maxHeight={440}>
          <ScrollArea>
            <AdvancedFilters config={issueFilterConfig} filters={query} />
          </ScrollArea>
        </Box>
      </PopoverPopup>
    </Popover>
  );
}
